import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context';
import portalAdminService from '../../services/portalAdminService';
import portalService from '../../services/portalService';
import PortalShell from './PortalShell';

/**
 * /portal/admin — the room. Three counter cards + recent activity feed.
 * Owner / admin only — guarded both by frontend (redirect away) and backend
 * (portal_role check in PortalAdminService.AssertAdminAsync).
 */
export default function PortalAdminHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverPortalRole, setServerPortalRole] = useState(null);
  // Prefer the live server response over the (possibly stale) JWT payload.
  // Falls back to the AuthContext snapshot while the request is in flight.
  const isOwner = (serverPortalRole ?? user?.portalRole) === 'owner';

  useEffect(() => {
    portalAdminService.getHome().then((r) => {
      if (r.success) setData(r.data);
      else setError(r.error || 'Could not load');
      setLoading(false);
    });
  }, []);

  // Frontend guard — backend is authoritative; we use portalService.getState()
  // so a stale JWT-side user payload can't cause a redirect loop with /portal.
  useEffect(() => {
    let cancelled = false;
    portalService.getState().then((r) => {
      if (cancelled) return;
      const role = r?.data?.portalRole;
      setServerPortalRole(role);
      if (role !== 'admin' && role !== 'owner') {
        navigate('/noir', { replace: true });
      }
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const pending = data?.pendingApplications ?? 0;
  const dotColor = (type) => (type === 'application_reviewed' ? 'var(--gold)' : 'var(--cream-dim)');

  return (
    <PortalShell viewerState="accepted">
      <div className="wrap" style={{ paddingTop: 'clamp(40px,7vw,80px)', paddingBottom: 100 }}>
        <section className="hero enter" style={{ marginBottom: 50 }}>
          <span className="kicker kicker--gold">ADMIN</span>
          <h1 className="display" style={{ fontSize: 'clamp(52px,8vw,108px)', marginTop: 20 }}>The back room<span className="dot">.</span></h1>
        </section>

        {error && <p className="meta" style={{ color: '#B4736A' }}>{error}</p>}
        {loading ? <p className="meta" style={{ color: 'var(--cream-faint)' }}>LOADING…</p> : (
          <>
            <div className="stats enter enter-2">
              <div className="stat">
                <div className={'stat__n' + (pending > 0 ? ' stat__n--gold' : '')}>{String(pending).padStart(2, '0')}</div>
                <div className="stat__l">Waiting at the door</div>
                {pending > 0
                  ? <Link className="alink" to="/noir/admin/applications?status=pending">REVIEW →</Link>
                  : <span className="meta" style={{ color: 'var(--cream-faint)' }}>NOBODY KNOCKING</span>}
              </div>
              <div className="stat">
                <div className="stat__n">{String(data?.members ?? 0).padStart(2, '0')}</div>
                <div className="stat__l">In the room</div>
                <Link className="alink" to="/noir/admin/members">MANAGE →</Link>
              </div>
              <div className="stat">
                <div className="stat__n">{String(data?.admins ?? 0).padStart(2, '0')}</div>
                <div className="stat__l">Hold the keys</div>
                {isOwner
                  ? <Link className="alink" to="/noir/admin/admins">MANAGE →</Link>
                  : <span className="meta" style={{ color: 'var(--cream-faint)' }}>{data?.admins ?? 0} TOTAL</span>}
              </div>
            </div>

            <div className="secthead enter enter-3"><span className="kicker">LATELY</span></div>
            <div className="enter enter-3">
              {(data?.recentActivity || []).length === 0 ? (
                <p className="meta" style={{ padding: '20px 6px', color: 'var(--cream-faint)' }}>NOTHING TO REPORT</p>
              ) : (
                (data?.recentActivity || []).map((row, i) => (
                  <div className="actrow" key={i}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span className="actdot" style={{ background: dotColor(row.type) }} />
                      <div>
                        <div className="actrow__t">{row.summary}</div>
                        {row.actorDisplayName && <div className="actrow__by">BY {row.actorDisplayName?.toUpperCase?.()}</div>}
                      </div>
                    </div>
                    {row.relatedApplicationId && <Link className="alink" to={`/noir/admin/applications/${row.relatedApplicationId}`}>OPEN →</Link>}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </PortalShell>
  );
}
